import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiagnosticContext, useSessionStorage, testHasLevel } from '../../helpers/functions';
import Tabs from '../Tabs';
import Container from '../../elements/container';
import PageHeading from '../../elements/page-heading';
import Checkbox from '../../elements/checkbox';
import Button from '../../elements/button';
import Alert from '../../elements/alert';
import { selfAssessmentQuestions } from '../../services/self-assessment-questions';

/* ─────────── constants ─────────────────────────────────────────── */
const RADIO_VALUES = [1, 2, 3, 4]; // Likert 1-4
const REQUIRED_LEVELS = ['k1', 'k3']; // KS1 + KS3 must exist
  /* ── domain + level scaffolding ────────────────── */
const DOMAINS = [
  { key: 'Reading', label: 'Lesen', color: 'text-yellow-400' },
  { key: 'Writing', label: 'Schreiben', color: 'text-green-400' },
  { key: 'Counting', label: 'Rechnen', color: 'text-blue-400' },
];
/* direkt unter den Konstanten oben im File ───────────── */
const SCALE_LABELS = {
  1: 'wird nicht/kaum beherrscht',
  2: 'wird ansatzweise beherrscht',
  3: 'wird überwiegend beherrscht',
  4: 'wird sicher beherrscht',
};

function LikertLegend() {
  return (
    /* ➜ schiebt Box nach rechts und setzt Hintergrund */
    <div
      className="self-end w-fit bg-gray-100 rounded-md px-3 py-1
                    text-xs text-gray-600 flex flex-wrap gap-x-4"
    >
      {RADIO_VALUES.map((v) => (
        <span key={v} className="whitespace-nowrap">
          <strong>{v}</strong> = {SCALE_LABELS[v]}
        </span>
      ))}
    </div>
  );
}

/* ─────────── little helper components ──────────────────────────── */
function LikertRow({ text, value, onChange, name, disabled = false }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center mt-2">
      <p className="sm:w-2/3 text-sm">{text}</p>
      <div className="flex gap-4">
        {RADIO_VALUES.map((v) => (
          <label key={v} className="inline-flex items-center gap-1">
            <input
              type="radio"
              className="diagnostics-results"
              name={name}
              value={v}
              checked={value === v}
              onChange={() => onChange(v)}
              disabled={disabled}
            />
            {v}
          </label>
        ))}
      </div>
    </div>
  );
}

const levelKeyToNumber = (k) => Number(k.replace('k', ''));

/* ──────────── page ─────────────────────────────────────────────── */
export default function SelfAssessmentScreen() {
  /* …context & navigation … */
  const { dispatch, state } = useContext(DiagnosticContext);
  const navigate = useNavigate();

  const makeAnswers = () => ({ k1: Array(5).fill(null), k2: Array(5).fill(null), k3: Array(5).fill(null), k4: Array(5).fill(null) });
  const domainDefaults = DOMAINS.reduce((acc, { key }) => {
    acc[key] = {
      k1: !testHasLevel(state, key, 1),
      k2: false,
      k3: !testHasLevel(state, key, 3),
      k4: false,
    };
    return acc;
  }, {});

  /* ── auto-select domains with existing tests ────── */
  const initialDomainChecked = {
    Reading: !!(state.readingTestId || state.readingSecondTestId || state.readingThirdTestId),
    Writing: !!(state.writingTestId || state.writingSecondTestId || state.writingThirdTestId),
    Counting: !!(state.countingTestId || state.countingSecondTestId || state.countingThirdTestId),
  };

  /* ── state persisted per session ────────────────── */
  const [domainChecked, setDomainChecked] = useSessionStorage('saDomainChecked', initialDomainChecked);
  const [levelVisible, setLevelVisible] = useSessionStorage('saLevelVisible', domainDefaults);
  const [answers, setAnswers] = useSessionStorage('saAnswers', { Reading: makeAnswers(), Writing: makeAnswers(), Counting: makeAnswers() });

  /* ── auto-select domains with existing tests on mount ──── */
  useEffect(() => {
    let shouldUpdate = false;
    const updatedDomainChecked = { ...domainChecked };

    // Check Reading domain
    const hasReadingTests = state.readingTestId || state.readingSecondTestId || state.readingThirdTestId;
    if (hasReadingTests && !domainChecked.Reading) {
      updatedDomainChecked.Reading = true;
      shouldUpdate = true;
    }

    // Check Writing domain
    const hasWritingTests = state.writingTestId || state.writingSecondTestId || state.writingThirdTestId;
    if (hasWritingTests && !domainChecked.Writing) {
      updatedDomainChecked.Writing = true;
      shouldUpdate = true;
    }

    // Check Counting domain
    const hasCountingTests = state.countingTestId || state.countingSecondTestId || state.countingThirdTestId;
    if (hasCountingTests && !domainChecked.Counting) {
      updatedDomainChecked.Counting = true;
      shouldUpdate = true;
    }

    const updatedLevels = { ...levelVisible };
    let levelChanged = false;
    DOMAINS.forEach(({ key }) => {
      const shouldShowK1 = !testHasLevel(state, key, 1);
      const shouldShowK3 = !testHasLevel(state, key, 3);
      if (updatedLevels[key].k1 !== shouldShowK1) {
        updatedLevels[key].k1 = shouldShowK1;
        levelChanged = true;
      }
      if (updatedLevels[key].k3 !== shouldShowK3) {
        updatedLevels[key].k3 = shouldShowK3;
        levelChanged = true;
      }
    });

    if (shouldUpdate) {
      setDomainChecked(updatedDomainChecked);
    }
    if (levelChanged) {
      setLevelVisible(updatedLevels);
    }
  }, [state, domainChecked, levelVisible, setDomainChecked, setLevelVisible]);

  /* ── local UI state (non-persistent) ────────────── */
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({ Reading: {}, Writing: {}, Counting: {} });
  const [domainError, setDomainError] = useState(false);
  const [ksError, setKsError] = useState(false);

  /* ── refs for scrolling to first block w/ errors ─── */
  const refs = {
    Reading: useRef(null),
    Writing: useRef(null),
    Counting: useRef(null),
  };
  const scrollTo = (r) => r?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  /* ──────────────────── handlers ─────────────────── */
  const toggleDomain = (d) => (e) => {
    const checked = e.target.checked;
    setDomainChecked((prev) => ({ ...prev, [d]: checked }));
    if (!checked) {
      // wipe downstream state
      setLevelVisible((prev) => ({ ...prev, [d]: { ...domainDefaults[d] } }));
      setAnswers((prev) => ({ ...prev, [d]: makeAnswers() }));
      dispatch({ type: `clear${d}` });
    }
  };

  const toggleLevel = (domain, lvl) => (e) => {
    const active = e.target.checked;
    setLevelVisible((p) => ({ ...p, [domain]: { ...p[domain], [lvl]: active } }));
    if (!active) {
      // deaktiviert ⇒ Antworten löschen
      setAnswers((p) => ({ ...p, [domain]: { ...p[domain], [lvl]: Array(5).fill(null) } }));
    }
  };

  const removeLevel = (domain, k) => {
    setLevelVisible((prev) => ({ ...prev, [domain]: { ...prev[domain], [k]: false } }));
    // clear answers
    setAnswers((prev) => ({ ...prev, [domain]: { ...prev[domain], [k]: Array(5).fill(null) } }));
  };

  const answerQuestion = (domain, level, idx) => (val) => {
    setAnswers((prev) => ({
      ...prev,
      [domain]: {
        ...prev[domain],
        [level]: prev[domain][level].map((v, i) => (i === idx ? val : v)),
      },
    }));
  };

  /*
  //to add to the button disabled={!isFormComplete()}     
  const isFormComplete = () => {
    if (!Object.values(domainChecked).some(Boolean)) return false;

    // KS-1 + KS-3 globally present?
    const globalK1 = DOMAINS.some(({ key }) => domainChecked[key] && levelVisible[key].k1);
    const globalK3 = DOMAINS.some(({ key }) => domainChecked[key] && levelVisible[key].k3);
    if (!hasRequiredLevels(state) && !(globalK1 && globalK3)) return false;

    // unanswered required questions?
  for (const { key: dom } of DOMAINS) {
    if (!domainChecked[dom]) continue;
    for (const [lvl, visible] of Object.entries(levelVisible[dom])) {
      if (visible && answers[dom][lvl].some((v) => v == null)) {
        return false;                // ⇦ irgendein aktiviertes Level unvollständig
      }
    }
  }
    return true;
  };
*/

  /* ─────────────────── validation ────────────────── */
  const validate = () => {
    let ok = true;

    /* (1) at least one domain */
    if (!Object.values(domainChecked).some(Boolean)) {
      setDomainError(true);
      ok = false;
    } else setDomainError(false);

    /* (2) unanswered required questions */
    const newErr = { Reading: {}, Writing: {}, Counting: {} };
    DOMAINS.forEach(({ key: dom }) => {
      Object.entries(levelVisible[dom]).forEach(([level, visible]) => {
        if (visible && domainChecked[dom]) {
          // ⇦ egal ob k1…k4
          const questionCount = selfAssessmentQuestions[dom][level].length;
          const hasNull = answers[dom][level].slice(0, questionCount).some((v) => v === null);
          newErr[dom][level] = hasNull;
          if (hasNull && ok) {
            scrollTo(refs[dom]);
            ok = false;
          }
        }
      });
    });
    setErrors(newErr);

    /* (3) KS1 and KS3 must exist for each selected domain */
    const allDomainsOk = DOMAINS.every(({ key: dom }) => {
      if (!domainChecked[dom]) return true;
      const k1Count = selfAssessmentQuestions[dom].k1.length;
      const k3Count = selfAssessmentQuestions[dom].k3.length;
      const hasK1 = testHasLevel(state, dom, 1) || (levelVisible[dom].k1 && !answers[dom].k1.slice(0, k1Count).some((v) => v === null));
      const hasK3 = testHasLevel(state, dom, 3) || (levelVisible[dom].k3 && !answers[dom].k3.slice(0, k3Count).some((v) => v === null));
      return hasK1 && hasK3;
    });

    if (!allDomainsOk) {
      setKsError(true);
      if (ok) {
        const firstBad = DOMAINS.find(
          ({ key: dom }) => {
            const k1Count = selfAssessmentQuestions[dom].k1.length;
            const k3Count = selfAssessmentQuestions[dom].k3.length;
            return domainChecked[dom] &&
            ((!testHasLevel(state, dom, 1) && (!levelVisible[dom].k1 || answers[dom].k1.slice(0, k1Count).some((v) => v === null))) ||
              (!testHasLevel(state, dom, 3) && (!levelVisible[dom].k3 || answers[dom].k3.slice(0, k3Count).some((v) => v === null))));
          }
        );
        if (firstBad) scrollTo(refs[firstBad.key]);
      }
      ok = false;
    } else setKsError(false);

    return ok;
  };

  /* re-validate once user changes something after first submit */
  useEffect(() => {
    if (submitted) validate();
  }, [domainChecked, levelVisible, answers]); // eslint-disable-line

  /* ─────────────────── submit ─────────────────────── */
  /* ───────── mapping Durchschnitt (1-4) ➜ Prozentrang-Ersatz ──────────── */
  const avgToPr = (avg) => {
    // avg is between 1 (hoher Förderbedarf) and 4 (kein Förderbedarf)
    // Invalid or missing data
    if (avg < 1 || avg > 4 || isNaN(avg)) return null;
    
    // Map average to PR values (questionnaire 1 = high need, 4 = no need)
    if (avg <= 1.75) return 5;   // avg 1.0-1.75 ⇒ PR 0-10  ⇒ hoher Förderbedarf
    if (avg <= 2.5)  return 15;  // avg 1.75-2.5 ⇒ PR 11-20 ⇒ mittlerer Förderbedarf
    if (avg <= 3.25) return 25;  // avg 2.5-3.25 ⇒ PR 21-30 ⇒ niedriger Förderbedarf
    return 35;                   // avg 3.25-4.0 ⇒ PR >30   ⇒ kein Förderbedarf
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validate()) return;

    DOMAINS.forEach(({ key }) => {
      if (domainChecked[key]) {
        // Process selected domain
        Object.entries(levelVisible[key]).forEach(([level, visible]) => {
          if (visible) {
            // Calculate average of valid answers (1-4)
            const validAnswers = answers[key][level].filter(val => val !== null && val !== undefined);
            const avg = validAnswers.length > 0 
              ? validAnswers.reduce((a, b) => a + b, 0) / validAnswers.length 
              : null;
            const competenceLevel = avgToPr(avg);
            /* dispatch the result value directly */
            dispatch({
              type: `${key.toLowerCase()}K${levelKeyToNumber(level)}`,
              payload: competenceLevel,
            });
          } else {
            // Clear unselected levels within selected domain
            dispatch({
              type: `${key.toLowerCase()}K${levelKeyToNumber(level)}`,
              payload: -1,
            });
          }
        });
      } else {
        // Clear all levels for unselected domain
        ['k1', 'k2', 'k3', 'k4'].forEach((level) => {
          dispatch({
            type: `${key.toLowerCase()}K${levelKeyToNumber(level)}`,
            payload: null,
          });
        });
      }
    });
    dispatch({ type: 'screenResultsEstimated', payload: true });
    navigate('/funding-recomendations');
  };

  /* ────────────────────── render ──────────────────── */
  const QuestionBlock = ({ domain, level }) => (
    <div className="mt-3 space-y-2">
      {selfAssessmentQuestions[domain][level].map((q, idx) => (
        <LikertRow
          key={idx}
          text={q}
          name={`${domain}-${level}-${idx}`}
          value={answers[domain][level][idx]}
          onChange={answerQuestion(domain, level, idx)}
        />
      ))}
      <LikertLegend /> {/* ✅ einmal pro KS-Block */}
      {errors[domain]?.[level] && <Alert type="error">Alle Fragen beantworten</Alert>}
      {['k2', 'k4'].includes(level) && (
        <button type="button" className="text-xs underline mt-1" onClick={() => removeLevel(domain, level)}>
          Frageblock entfernen
        </button>
      )}
    </div>
  );

  return (
    <>
      <Tabs />
      <Container className="flex flex-col items-center">
        <PageHeading stretch title="Selbsteinschätzung" />

        <form onSubmit={handleSubmit} className="flex flex-col gap-y-10 w-full max-w-4xl">
          {DOMAINS.map(({ key: dom, label }) => (
            <div key={dom} ref={refs[dom]} className="space-y-6">
              {/* domain toggle */}
              <Checkbox name={dom} size="sm" label={label} checked={domainChecked[dom]} onChange={toggleDomain(dom)} />

              {/* accordion: required */}
              {domainChecked[dom] &&
                REQUIRED_LEVELS.some((lvl) => levelVisible[dom][lvl] || testHasLevel(state, dom, levelKeyToNumber(lvl))) && (
                  <details open className="border border-gray-300 rounded">
                    <summary className="bg-gray-100 px-4 py-2 font-medium cursor-pointer">Erforderliche Fragen</summary>
                    <div className="p-4">
                      {REQUIRED_LEVELS.map((lvl) => {
                        const levelNum = levelKeyToNumber(lvl);
                        if (testHasLevel(state, dom, levelNum)) {
                          return (
                            <div key={lvl} className="mb-6">
                              <h4 className="font-semibold mb-1">Kompetenzstufe&nbsp;{levelNum}</h4>
                              <Alert type="info">Testergebnisse liegen vor. Selbstbeurteilung nicht erforderlich.</Alert>
                            </div>
                          );
                        }
                        if (!levelVisible[dom][lvl]) return null;
                        return (
                          <div key={lvl} className="mb-6">
                            <h4 className="font-semibold mb-1">Kompetenzstufe&nbsp;{levelNum}</h4>
                            <QuestionBlock domain={dom} level={lvl} />
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}

              {/* accordion: optional */}
              {domainChecked[dom] && (
                <details className="border border-gray-300 rounded">
                  <summary className="bg-gray-100 px-4 py-2 font-medium cursor-pointer">Optionale Fragen</summary>
                  <div className="p-4 space-y-6">
                    {['k2', 'k4'].map((lvl) => {
                      const active = levelVisible[dom][lvl];
                      return (
                        <div key={lvl}>
                          {/* Aktivierungs-Checkbox */}
                          <Checkbox
                            size="sm"
                            name={`${dom}-${lvl}-enable`}
                            label={`Kompetenzstufe ${levelKeyToNumber(lvl)} nutzen`}
                            checked={active}
                            onChange={toggleLevel(dom, lvl)}
                          />

                          {/* Fragenblock – nur sichtbar, wenn aktiv */}
                          {active && (
                            <div className="ml-4 mt-2">
                              {selfAssessmentQuestions[dom][lvl].map((q, idx) => (
                                <LikertRow
                                  key={idx}
                                  text={q}
                                  name={`${dom}-${lvl}-${idx}`}
                                  value={answers[dom][lvl][idx]}
                                  onChange={answerQuestion(dom, lvl, idx)}
                                />
                              ))}
                              <LikertLegend />
                              {errors[dom]?.[lvl] && <Alert type="error">Alle Fragen beantworten</Alert>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          ))}

          {domainError && <Alert type="error">Mindestens ein Lernbereich muss ausgewählt sein.</Alert>}
          {ksError && (
            <Alert type="error">
              Bitte füllen Sie in allen ausgewählten Bereichen die <strong>Kompetenzstufen&nbsp;1&nbsp;und&nbsp;3</strong> aus (oder wählen Sie
              ein Testverfahren, in dem die Stufen bereits vorliegen).
            </Alert>
          )}

          {/* submit */}
          <Button size="sm" type="submit" className="my-6 self-end">
            Weiter
          </Button>
        </form>
      </Container>
    </>
  );
}
